import React, { useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MODES = [
  { key: 'build', label: 'Build a BST' },
  { key: 'traverse', label: 'Traversal Types' },
  { key: 'search', label: 'Search in a BST' },
  { key: 'height', label: 'Height & Balance' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

/* ─── BST helpers ────────────────────────────────────────────────── */
const insertBST = (node, val) => {
  if (!node) return { val, left: null, right: null };
  if (val < node.val) return { ...node, left: insertBST(node.left, val) };
  if (val > node.val) return { ...node, right: insertBST(node.right, val) };
  return node;
};
const preorder = (node, out = []) => { if (!node) return out; out.push(node.val); preorder(node.left, out); preorder(node.right, out); return out; };
const inorder = (node, out = []) => { if (!node) return out; inorder(node.left, out); out.push(node.val); inorder(node.right, out); return out; };
const postorder = (node, out = []) => { if (!node) return out; postorder(node.left, out); postorder(node.right, out); out.push(node.val); return out; };
const levelorder = node => {
  const out = []; if (!node) return out;
  const q = [node];
  while (q.length) { const n = q.shift(); out.push(n.val); if (n.left) q.push(n.left); if (n.right) q.push(n.right); }
  return out;
};

/* ─── Recursive tree view with a "current" highlight ─────────────── */
const TreeView = ({ node, current, visited }) => {
  if (!node) return <span style={{ color: 'var(--muted)', fontSize: 11.5, fontFamily: 'monospace' }}>null</span>;
  const isCurrent = current === node.val;
  const isVisited = visited && visited.includes(node.val);
  return (
    <div>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 42, height: 42, padding: '0 10px', borderRadius: 10,
        background: isCurrent ? `linear-gradient(135deg,${V1},${V2})` : isVisited ? 'var(--bg2)' : 'var(--bg3)',
        border: `1.5px solid ${isCurrent ? V1 : isVisited ? GREEN : 'var(--border)'}`,
        color: isCurrent ? '#fff' : 'var(--text)', fontWeight: 800, fontSize: 15,
        fontFamily: "'Inter',sans-serif", transition: 'all 0.2s ease',
      }}>{node.val}</span>
      {(node.left || node.right) && (
        <div style={{ marginLeft: 18, paddingLeft: 16, marginTop: 10, borderLeft: '2px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {node.left && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: V2, marginTop: 12, flexShrink: 0 }}>L</span>
              <TreeView node={node.left} current={current} visited={visited} />
            </div>
          )}
          {node.right && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: TEAL, marginTop: 12, flexShrink: 0 }}>R</span>
              <TreeView node={node.right} current={current} visited={visited} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const smallInput = {
  width: 90, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = disabled => ({
  padding: '8px 14px', borderRadius: 9, cursor: disabled ? 'default' : 'pointer',
  border: `1.5px solid ${disabled ? 'var(--border)' : V1}`, background: 'transparent',
  color: disabled ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
  fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={ghostBtn(step === 0)}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);
const Chip = ({ children, color = V1 }) => (
  <span style={{
    display: 'inline-flex', padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: `${color}15`, color, marginRight: 6, marginBottom: 6, fontFamily: 'monospace',
  }}>{children}</span>
);

const DEFAULT_VALUES = [50, 30, 70, 20, 40, 60, 80];
const buildDefault = () => DEFAULT_VALUES.reduce((t, v) => insertBST(t, v), null);

/* ─── Build a BST ────────────────────────────────────────────────── */
const BuildMode = () => {
  const [tree, setTree] = useState(buildDefault());
  const [value, setValue] = useState('');
  const [lastInserted, setLastInserted] = useState(null);

  const insert = () => {
    if (value.trim() === '' || isNaN(Number(value))) return;
    const v = Number(value);
    setTree(t => insertBST(t, v));
    setLastInserted(v);
    setValue('');
  };
  const reset = () => { setTree(null); setLastInserted(null); };

  const code = {
    lines: [
      'ListNode insert(TreeNode node, int val) {',
      '    if (node == null) return new TreeNode(val);',
      '    if (val < node.val) node.left = insert(node.left, val);',
      '    else if (val > node.val) node.right = insert(node.right, val);',
      '    return node;',
      '}',
    ],
    active: lastInserted === null ? -1 : 2,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <div style={{ paddingBottom: 18, overflowX: 'auto' }}>
          <TreeView node={tree} current={lastInserted} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <input value={value} onChange={e => setValue(e.target.value)} style={smallInput} placeholder="value" />
            <button onClick={insert} style={smallBtn(V1)}>Insert</button>
            <button onClick={reset} style={smallBtn(RED)}>Clear Tree</button>
          </div>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>
            {lastInserted === null ? 'Insert a value — it finds its own spot based on the BST rule: smaller goes left, bigger goes right.' : `${lastInserted} was compared down from the root until it found an empty spot.`}
          </p>
        </div>
      </div>
      <CodePanel title="Java — BST Insertion" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Traversal Types ────────────────────────────────────────────── */
const TRAVERSALS = [
  { key: 'pre', label: 'Preorder', fn: preorder },
  { key: 'in', label: 'Inorder', fn: n => inorder(n) },
  { key: 'post', label: 'Postorder', fn: postorder },
  { key: 'level', label: 'Level-Order', fn: levelorder },
];
const TRAVERSAL_CODE = {
  pre: ['void preorder(TreeNode node) {', '    if (node == null) return;', '    visit(node.val);       // visit BEFORE recursing', '    preorder(node.left);', '    preorder(node.right);', '}'],
  in: ['void inorder(TreeNode node) {', '    if (node == null) return;', '    inorder(node.left);', '    visit(node.val);       // visit BETWEEN children', '    inorder(node.right);', '}'],
  post: ['void postorder(TreeNode node) {', '    if (node == null) return;', '    postorder(node.left);', '    postorder(node.right);', '    visit(node.val);       // visit AFTER recursing', '}'],
  level: ['Queue<TreeNode> queue = new LinkedList<>();', 'queue.add(root);', 'while (!queue.isEmpty()) {', '    TreeNode node = queue.poll();', '    visit(node.val);', '    if (node.left != null) queue.add(node.left);', '    if (node.right != null) queue.add(node.right);', '}'],
};
const TraverseMode = () => {
  const [tree] = useState(buildDefault());
  const [type, setType] = useState('pre');
  const [step, setStep] = useState(0);

  const order = TRAVERSALS.find(t => t.key === type).fn(tree);
  const cur = order[step];
  const visitedSoFar = order.slice(0, step + 1);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {TRAVERSALS.map(t => (
            <button key={t.key} onClick={() => { setType(t.key); setStep(0); }} style={{
              padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${type === t.key ? V1 : 'var(--border)'}`,
              background: type === t.key ? V1 : 'transparent', color: type === t.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}>{t.label}</button>
          ))}
        </div>
        <div style={{ paddingBottom: 18, overflowX: 'auto' }}>
          <TreeView node={tree} current={cur} visited={visitedSoFar} />
        </div>
        <StepNav step={step} total={order.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(order.length - 1, s + 1))} />
        <div style={{ margin: '14px 0' }}>
          {order.map((v, i) => <Chip key={i} color={i <= step ? GREEN : 'var(--muted)'}>{v}</Chip>)}
        </div>
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>
          Visiting node {cur} — step {step + 1} of {order.length} in {TRAVERSALS.find(t => t.key === type).label} order.
        </p>
      </div>
      <CodePanel title={`Java — ${TRAVERSALS.find(t => t.key === type).label}`} lines={TRAVERSAL_CODE[type]} active={type === 'level' ? 4 : (type === 'pre' ? 2 : type === 'in' ? 3 : 4)} />
    </div>
  );
};

/* ─── Search in a BST ────────────────────────────────────────────── */
const SearchMode = () => {
  const [tree] = useState(buildDefault());
  const [targetInput, setTargetInput] = useState('60');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const startSearch = () => {
    const target = Number(targetInput);
    if (isNaN(target)) return;
    const built = [];
    let node = tree;
    while (node) {
      if (target === node.val) { built.push({ val: node.val, result: 'found' }); break; }
      if (target < node.val) { built.push({ val: node.val, result: 'left' }); node = node.left; }
      else { built.push({ val: node.val, result: 'right' }); node = node.right; }
    }
    if (built.length === 0 || built[built.length - 1].result !== 'found') built.push({ done: true });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const visitedSoFar = steps ? steps.slice(0, step + 1).filter(s => !s.done).map(s => s.val) : [];
  const note = !cur ? 'Enter a target value, then Search.' :
    cur.done ? 'Hit a null child — the target isn\'t in this tree.' :
    cur.result === 'found' ? `${cur.val} matches — found!` :
    cur.result === 'left' ? `${targetInput} < ${cur.val} — go left (everything in this subtree is smaller).` :
    `${targetInput} > ${cur.val} — go right (everything in this subtree is bigger).`;

  const code = {
    lines: [
      'TreeNode search(TreeNode node, int target) {',
      '    if (node == null || node.val == target) return node;',
      '    if (target < node.val) return search(node.left, target);',
      '    return search(node.right, target);',
      '}',
    ],
    active: !cur ? -1 : cur.done ? 1 : cur.result === 'found' ? 1 : cur.result === 'left' ? 2 : 3,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <div style={{ paddingBottom: 18, overflowX: 'auto' }}>
          <TreeView node={tree} current={cur && !cur.done ? cur.val : null} visited={visitedSoFar} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Target</span>
            <input value={targetInput} onChange={e => { setTargetInput(e.target.value); setSteps(null); }} style={smallInput} />
            <button onClick={startSearch} style={smallBtn(V1)}>Search</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
          <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: 0 }}>Notice only one path from root to leaf is ever explored — the BST property rules out an entire subtree at every step, the same halving idea as binary search.</p>
        </div>
      </div>
      <CodePanel title="Java — BST Search" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Height & Balance ───────────────────────────────────────────── */
const computeHeights = tree => {
  const steps = [];
  const helper = node => {
    if (!node) return 0;
    const lh = helper(node.left);
    const rh = helper(node.right);
    const h = 1 + Math.max(lh, rh);
    const balanced = Math.abs(lh - rh) <= 1;
    steps.push({ val: node.val, lh, rh, h, balanced });
    return h;
  };
  helper(tree);
  return steps;
};
const HeightMode = () => {
  const [tree] = useState(buildDefault());
  const [steps] = useState(() => computeHeights(tree));
  const [step, setStep] = useState(0);

  const cur = steps[step];
  const overallBalanced = steps.every(s => s.balanced);
  const isLast = step === steps.length - 1;

  const code = {
    lines: [
      'int height(TreeNode node) {',
      '    if (node == null) return 0;',
      '    int lh = height(node.left);',
      '    int rh = height(node.right);',
      '    if (Math.abs(lh - rh) > 1) balanced = false;',
      '    return 1 + Math.max(lh, rh);',
      '}',
    ],
    active: 4,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <div style={{ paddingBottom: 18, overflowX: 'auto' }}>
          <TreeView node={tree} current={cur.val} />
        </div>
        <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />
        <div style={{ margin: '14px 0' }}>
          <Chip color={V2}>node {cur.val}</Chip>
          <Chip color={TEAL}>left height = {cur.lh}</Chip>
          <Chip color={TEAL}>right height = {cur.rh}</Chip>
          <Chip color={GREEN}>this node's height = {cur.h}</Chip>
          <Chip color={cur.balanced ? GREEN : RED}>{cur.balanced ? 'balanced here' : 'UNBALANCED here'}</Chip>
        </div>
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>
          Heights are computed bottom-up (post-order) — a node needs both children's heights before it can compute its own.
          {isLast && ` Every node checked: this tree is ${overallBalanced ? 'balanced' : 'NOT balanced'} overall.`}
        </p>
      </div>
      <CodePanel title="Java — Height & Balance Check" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const QUIZ_TREE = buildDefault();
const buildQuizScenario = () => {
  const target = DEFAULT_VALUES[Math.floor(Math.random() * DEFAULT_VALUES.length)];
  const path = [50, 30, 70, 20, 40, 60, 80];
  const node = path[Math.floor(Math.random() * 3)]; // one of the first 3 nodes reachable
  let correct;
  if (target === node) correct = 'found';
  else if (target < node) correct = 'left';
  else correct = 'right';
  return { node, target, correct };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  const options = [
    { key: 'left', label: 'Go left (target is smaller)' },
    { key: 'right', label: 'Go right (target is bigger)' },
    { key: 'found', label: 'Found it — this IS the target' },
  ];

  return (
    <div>
      <div style={{ paddingBottom: 18, overflowX: 'auto' }}>
        <TreeView node={QUIZ_TREE} current={scenario.node} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 14 }}>
        Searching for {scenario.target}. You're standing at node {scenario.node}. What next?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {options.map(o => {
          const revealed = answer !== null;
          const isCorrect = o.key === scenario.correct;
          const isPicked = o.key === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={o.key} onClick={() => pick(o.key)} disabled={revealed} style={{
              textAlign: 'left', padding: '10px 14px', borderRadius: 10,
              border: `1.5px solid ${border}`, background: bg, color, fontSize: 13.5,
              cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}>{o.label}</button>
          );
        })}
      </div>
      {answer !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={next} style={smallBtn(V1)}>Next Question</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
        </div>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const TreePlayground = () => {
  const [mode, setMode] = useState('build');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'build' && <BuildMode />}
      {mode === 'traverse' && <TraverseMode />}
      {mode === 'search' && <SearchMode />}
      {mode === 'height' && <HeightMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default TreePlayground;
